import { useParams, Link, Navigate } from "react-router-dom";
import { getArticleBySlug } from "@/data/solarArticles";
import SEOHead from "@/components/SEOHead";
import ArticleLayout from "@/components/ArticleLayout";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import ProductCard from "@/components/ProductCard";
import ComparisonTable from "@/components/ComparisonTable";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import { AlertTriangle } from "lucide-react";
import { LEGACY_GUIDE_REDIRECTS } from "@/data/legacyGuideRedirects";
import { sourcesForArticle } from "@/lib/articleSources";

function mapGeneratedArticle(article) {
  return {
    ...article,
    metaTitle: article.meta_title,
    metaDescription: article.meta_description,
    quickAnswer: article.quick_answer,
    relatedSlugs: article.related_slugs || [],
    relatedPaths: article.related_paths || [],
    tags: article.tags || [],
    faqs: article.faqs || [],
    sections: article.sections || [],
    heroImageUrl: article.hero_image_url || "",
    heroImageAlt: article.hero_image_alt || "",
    publishedAt: article.published_at || article.created_date || "",
    updatedAt: article.updated_date || article.published_at || article.created_date || "",
    targetKeyword: article.target_keyword || "",
    keywordCluster: article.keyword_cluster || "",
    isGenerated: true,
  };
}

function buildSchema(article) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "url": `https://highestefficiencysolarcell.com/solar-guides/${article.slug}`,
    ...(article.heroImageUrl && { "image": article.heroImageUrl }),
    ...(article.publishedAt && { "datePublished": article.publishedAt }),
    ...(article.updatedAt && { "dateModified": article.updatedAt }),
    "author": {
      "@type": "Organization",
      "name": "Highest Efficiency Solar Cell Editorial Team",
      "url": "https://highestefficiencysolarcell.com/about#editorial-team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "HighestEfficiencySolarCell.com",
      "url": "https://highestefficiencysolarcell.com"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://highestefficiencysolarcell.com" },
        { "@type": "ListItem", "position": 2, "name": "Solar Guides", "item": "https://highestefficiencysolarcell.com/solar-guides" },
        { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://highestefficiencysolarcell.com/solar-guides/${article.slug}` },
      ]
    },
    ...(article.faqs?.length > 0 && {
      "mainEntity": article.faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": { "@type": "Answer", "text": faq.a }
      }))
    }),
  };
}

export default function SolarGuidePage() {
  const { slug } = useParams();
  const redirectTarget = LEGACY_GUIDE_REDIRECTS[slug];
  const staticArticle = getArticleBySlug(slug);

  const { data: generatedMatches = [], isLoading: isLoadingGenerated } = useQuery({
    queryKey: ["generated-blog-article", slug],
    queryFn: () => base44.entities.BlogArticle.filter({ slug, status: "published" }, "-published_at", 1),
    staleTime: 1000 * 60 * 5,
  });

  const articleBase = staticArticle || (generatedMatches[0] ? mapGeneratedArticle(generatedMatches[0]) : null);
  const article = articleBase ? {
    ...articleBase,
    sourceNotes: sourcesForArticle(articleBase),
  } : null;

  const { data: allProducts = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  if (!article && isLoadingGenerated) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <>
        <SEOHead
          title="Guide Not Found | HighestEfficiencySolarCell.com"
          description="The requested solar guide is no longer available. Browse our current solar panel guides and buyer resources."
          canonical="https://highestefficiencysolarcell.com/solar-guides"
          robots="noindex, follow"
        />
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h1 className="font-heading font-bold text-2xl mb-2">Guide Not Found</h1>
        <p className="text-muted-foreground mb-6">This guide doesn't exist yet.</p>
        <Link to="/solar-guides" className="inline-flex items-center gap-2 text-accent hover:underline font-medium">
          ← Back to Solar Guides
        </Link>
      </div>
      </>
    );
  }

  // Find relevant products by tag matching
  const relevantProducts = allProducts.filter((p) => {
    if (!p.amazon_url) return false;
    const tags = article.tags || [];
    const tagLower = tags.map((t) => t.toLowerCase());
    const recommendedIds = new Set(article.recommended_product_ids || []);
    if (recommendedIds.has(p.id)) return true;
    const productText = `${p.product_name} ${p.brand} ${p.best_for_category} ${p.panel_type} ${p.form_factor}`.toLowerCase();
    return tagLower.some((t) => productText.includes(t)) || tagLower.some((t) => (p.best_for_category || "").toLowerCase().includes(t));
  }).slice(0, 6);

  const schema = buildSchema(article);

  return (
    <>
      <SEOHead
        title={article.metaTitle}
        description={article.metaDescription}
        canonical={`https://highestefficiencysolarcell.com/solar-guides/${article.slug}`}
        ogImage={article.heroImageUrl || undefined}
        ogType="article"
        schema={schema}
      />

      <ArticleLayout article={article}>
        {/* Relevant Products Section */}
        {relevantProducts.length > 0 && (
          <div className="my-10 not-prose">
            <h2 className="font-heading font-bold text-2xl mb-2">Recommended Products</h2>
            <p className="text-muted-foreground text-sm mb-1">Based on listed specifications. Prices and availability change — check Amazon for current details.</p>
            <AffiliateDisclosure />
            
            {relevantProducts.length >= 3 ? (
              <ComparisonTable products={relevantProducts} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                {relevantProducts.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        )}
      </ArticleLayout>
    </>
  );
}